import React, { useEffect, useMemo, useState } from 'react';
import { Empty, Modal, Spin } from 'antd';
import { CalendarOutlined, FieldTimeOutlined, GiftOutlined, HeartOutlined, UserOutlined } from '@ant-design/icons';
import { reportsApi } from '../../utils/api';
import { REPORT_CHART_COLORS } from './chartFormatters';
import { padMonth } from './reportUtils';

function formatFaNum(v) {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  if (!Number.isNaN(n) && String(v).trim() !== '') return n.toLocaleString('fa-IR');
  return String(v);
}

const STAT_KEYS = {
  allocated: ['سهمیه_استحقاقی', 'allocated', 'total', 'استحقاقی'],
  used: ['استفاده_شده', 'used', 'مصرف_شده'],
  remaining: ['باقیمانده', 'remaining'],
};

function pickStat(obj, aliases) {
  if (!obj || typeof obj !== 'object') return null;
  for (const k of aliases) {
    if (obj[k] !== undefined && obj[k] !== null && obj[k] !== '') return obj[k];
  }
  return null;
}

const EXTRA_FIELD_LABELS = {
  انتقال_از_سال_قبل: 'انتقال از سال قبل',
  مرخصی_استعلاجی: 'مرخصی استعلاجی',
  کارمند: 'شناسه کارمند',
  سال: 'سال',
  ماه: 'ماه جاری',
};

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div
      className="rounded-xl border px-4 py-3 text-center shadow-sm transition-colors dark:border-slate-600"
      style={{
        borderColor: `${accent}55`,
        background: `linear-gradient(145deg, ${accent}18 0%, transparent 65%)`,
      }}
    >
      <div className="mb-2 flex justify-center text-slate-500 dark:text-slate-400">
        <Icon className="text-lg" style={{ color: accent }} />
      </div>
      <div className="text-xs font-medium text-slate-500 dark:text-slate-400">{label}</div>
      <div className="mt-1 text-xl font-semibold tabular-nums text-slate-800 dark:text-slate-100">{formatFaNum(value)}</div>
    </div>
  );
}

/**
 * @param {boolean} open
 * @param {() => void} onClose
 * @param {string|number} employeeId
 * @param {number|string} year
 * @param {number|string} month
 * @param {string} [monthLabel] نام فارسی ماه
 * @param {string} [employeeName] نام از ردیف جدول
 * @param {object} [rowSnapshot] ردیف جدول برای پشتیبان نمایش اعداد
 */
const LeaveQuotaDetailModal = ({ open, onClose, employeeId, year, month, monthLabel, employeeName, rowSnapshot }) => {
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!open || employeeId == null || employeeId === '') return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setDetail(null);
      try {
        const res = await reportsApi.getEmployeeLeaveQuotaDetail(employeeId, year, padMonth(month));
        const raw = res?.data ?? res ?? null;
        if (!cancelled) setDetail(raw && typeof raw === 'object' ? raw : null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [open, employeeId, year, month]);

  const stats = useMemo(() => {
    const d = detail && typeof detail === 'object' ? detail : null;
    const r = rowSnapshot && typeof rowSnapshot === 'object' ? rowSnapshot : null;
    return {
      allocated:
        pickStat(d, STAT_KEYS.allocated) ?? r?.allocated ?? r?.total,
      used: pickStat(d, STAT_KEYS.used) ?? r?.used,
      remaining: pickStat(d, STAT_KEYS.remaining) ?? r?.remaining,
    };
  }, [detail, rowSnapshot]);

  const extraEntries = useMemo(() => {
    if (!detail || typeof detail !== 'object') return [];
    const skip = new Set([
      ...STAT_KEYS.allocated,
      ...STAT_KEYS.used,
      ...STAT_KEYS.remaining,
      'کارمند',
      'employee_name',
      'سال',
      'ماه',
    ]);
    return Object.entries(detail).filter(([k]) => !skip.has(k));
  }, [detail]);

  const periodLine = [monthLabel, year].filter(Boolean).join(' ') || `${year}/${padMonth(month)}`;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={560}
      centered
      destroyOnClose
      className="leave-quota-detail-modal"
      styles={{
        content: { padding: 0, borderRadius: 16, overflow: 'hidden' },
        header: { margin: 0, padding: '16px 20px', borderBottom: '1px solid rgba(148,163,184,0.25)' },
        body: { padding: 0 },
      }}
      title={
        <div className="flex items-start gap-3 pe-8">
          <div
            className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-white shadow-md"
            style={{
              background: `linear-gradient(135deg, ${REPORT_CHART_COLORS.sky} 0%, ${REPORT_CHART_COLORS.teal} 100%)`,
            }}
          >
            <CalendarOutlined className="text-xl" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-base font-semibold text-slate-900 dark:text-slate-100 custom-font">
              جزئیات سهمیه مرخصی
            </div>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-slate-500 dark:text-slate-400">
              <span className="inline-flex items-center gap-1">
                <UserOutlined />
                {employeeName || detail?.کارمند || detail?.employee_name || `کارمند ${employeeId}`}
              </span>
              <span className="text-slate-300 dark:text-slate-600">·</span>
              <span>{periodLine}</span>
            </div>
          </div>
        </div>
      }
    >
      <div className="custom-font bg-gradient-to-b from-slate-50/90 to-white px-5 pb-6 pt-5 dark:from-slate-900 dark:to-slate-900/95">
        {loading && !(detail || rowSnapshot) ? (
          <div className="flex min-h-[220px] flex-col items-center justify-center gap-3 py-10">
            <Spin size="large" />
            <span className="text-sm text-slate-500">در حال بارگذاری جزئیات…</span>
          </div>
        ) : null}

        {detail || rowSnapshot ? (
          <>
            {loading ? (
              <div className="mb-4 flex items-center justify-center gap-2 rounded-lg bg-indigo-50/80 py-2 text-sm text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-200">
                <Spin size="small" />
                در حال دریافت جزئیات تکمیلی…
              </div>
            ) : null}

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <StatCard
                icon={GiftOutlined}
                label="سهمیه استحقاقی"
                value={stats.allocated}
                accent={REPORT_CHART_COLORS.sky}
              />
              <StatCard
                icon={FieldTimeOutlined}
                label="استفاده‌شده"
                value={stats.used}
                accent={REPORT_CHART_COLORS.teal}
              />
              <StatCard
                icon={HeartOutlined}
                label="باقی‌مانده"
                value={stats.remaining}
                accent={REPORT_CHART_COLORS.mint}
              />
            </div>

            {detail && extraEntries.length > 0 ? (
              <div className="mt-6 rounded-xl border border-slate-200/80 bg-white/80 p-4 shadow-sm dark:border-slate-600 dark:bg-slate-800/50">
                <div className="mb-3 text-xs font-semibold text-slate-400">سایر موارد</div>
                <ul className="m-0 list-none space-y-3 p-0">
                  {extraEntries.map(([key, value]) => (
                    <li
                      key={key}
                      className="flex items-start justify-between gap-4 border-b border-slate-100 pb-3 last:border-0 last:pb-0 dark:border-slate-700"
                    >
                      <span className="text-sm text-slate-600 dark:text-slate-300">
                        {EXTRA_FIELD_LABELS[key] || key.replace(/_/g, ' ')}
                      </span>
                      <span className="text-sm font-medium tabular-nums text-slate-900 dark:text-slate-100">
                        {typeof value === 'object' ? JSON.stringify(value) : formatFaNum(value)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </>
        ) : !loading ? (
          <Empty className="py-12" description="داده‌ای برای نمایش نیست" />
        ) : null}
      </div>
    </Modal>
  );
};

export default LeaveQuotaDetailModal;
